import { Base58Util } from "@solomon-labs/base58";

import type { ClaimApi, ClaimRequest, ClaimRewards } from "./claimTypes";

export type ClaimFetch = (path: string, init?: RequestInit) => Promise<Response>;
export class ClaimApiError extends Error {
    constructor(
        message: string,
        readonly status: number,
        readonly code?: string
    ) {
        super(message);
    }
}
export function createClaimApi(input: {
    audience: "main" | "org";
    account: string;
    scopeKey: string;
    fetch: ClaimFetch;
}): ClaimApi {
    const root = input.audience === "main" ? "/api/yaas" : "/api/org/client";
    const wallet =
        input.audience === "main"
            ? `${root}/wallet/${encodeURIComponent(input.account)}`
            : `${root}/wallets/${encodeURIComponent(input.account)}`;
    const query =
        input.audience === "main"
            ? `?address=${encodeURIComponent(input.account)}`
            : `?accountUuid=${encodeURIComponent(input.account)}`;
    let identity: { token: string; expiresAt: string; signer: string } | null = null;
    async function call<T>(path: string, body?: object): Promise<T> {
        const headers = new Headers({ "Content-Type": "application/json" });
        if (identity) {
            headers.set("X-Claim-Identity", identity.token);
            if (input.audience === "main") headers.set("Authorization", `Bearer ${identity.token}`);
        }
        const controller = new AbortController();
        // Preparation can include upstream transaction construction. Reads should
        // fail sooner so the next status poll can recover from a stalled request.
        const timer = setTimeout(() => controller.abort(), path.endsWith("/claims/prepare") ? 60_000 : 15_000);
        try {
            const response = await input.fetch(path, {
                method: body ? "POST" : "GET",
                headers,
                signal: controller.signal,
                ...(body ? { body: JSON.stringify(body) } : {})
            });
            if (!response.ok) {
                const error = (await response.json().catch(() => null)) as {
                    message?: string;
                    error?: string;
                    code?: string;
                } | null;
                throw new ClaimApiError(
                    error?.message ?? error?.error ?? "Claim request failed",
                    response.status,
                    error?.code
                );
            }
            return (await response.json()) as T;
        } catch (error) {
            if (controller.signal.aborted)
                throw new ClaimApiError(
                    "Claim request timed out. Check the claim status before trying again.",
                    408,
                    "claim_request_timeout"
                );
            // Preserve HTTP errors from authenticated app fetch helpers.
            if (
                error instanceof Error &&
                "status" in error &&
                typeof error.status === "number" &&
                Number.isInteger(error.status) &&
                error.status >= 400 &&
                error.status <= 599
            ) {
                throw new ClaimApiError(
                    error.message,
                    error.status,
                    "code" in error && typeof error.code === "string" ? error.code : undefined
                );
            }
            throw error;
        } finally {
            clearTimeout(timer);
        }
    }
    return {
        scopeKey: input.scopeKey,
        async authorize(signer): Promise<void> {
            if (identity && identity.signer === signer.address && Date.parse(identity.expiresAt) - Date.now() > 30_000)
                return;
            const challenge = await call<{ message: string; nonce: string }>(
                `${wallet}/claims/identity-session/challenge`,
                { signerAddress: signer.address }
            );
            const signature = Base58Util.encode(await signer.signMessage(new TextEncoder().encode(challenge.message)));
            const result = await call<{ token: string; expiresAt: string }>(`${wallet}/claims/identity-session`, {
                signerAddress: signer.address,
                nonce: challenge.nonce,
                signature
            });
            identity = { ...result, signer: signer.address };
        },
        rewards: () => call<ClaimRewards>(input.audience === "main" ? `${wallet}/rewards` : `${root}/rewards${query}`),
        prepare: (requestId) => call<ClaimRequest>(`${wallet}/claims/prepare`, { requestId }),
        report: (claimRequestUuid, attemptUuid, transactionSignature) =>
            call<ClaimRequest>(`${wallet}/claims/confirm`, {
                claimRequestUuid,
                attemptUuid,
                transactionSignature
            }),
        status: (claimRequestUuid) =>
            call<ClaimRequest>(`${root}/claims/${encodeURIComponent(claimRequestUuid)}${query}`)
    };
}
